'use client';

import React, { useEffect, useRef, useState } from 'react';
import { X, Crown, Check, Zap, Lock, Star, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface SubscriptionModalProps {
  onClose: () => void;
  articleTitle?: string;
}

const annualInclusions = [
  { text: 'Full access to all editorial articles', highlight: false },
  { text: 'Private Intelligence Desk', highlight: true },
  { text: 'Upcoming luxury concierge', highlight: true },
  { text: 'Save to private collections', highlight: true },
];

const monthlyInclusions = [
  { text: 'Access to editorial content', highlight: false },
];

export default function SubscriptionModal({ onClose, articleTitle }: SubscriptionModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'monthly'>('annual');
  const [visible, setVisible] = useState(false);
  const [payuLoading, setPayuLoading] = useState(false);
  const [payuError, setPayuError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const { user } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 20);
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const handleSubscribe = async () => {
    // If not logged in, redirect to sign-up first
    if (!user) {
      handleClose();
      router.push('/sign-up-login-screen');
      return;
    }

    setPayuLoading(true);
    setPayuError('');

    try {
      const res = await fetch('/api/payu/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to initiate payment');
      }

      const { payuUrl, formParams } = await res.json();

      // Build and submit a hidden form to PayU
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payuUrl;

      Object.entries(formParams).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err: any) {
      setPayuError(err.message || 'Payment initiation failed. Please try again.');
      setPayuLoading(false);
    }
  };

  const currentInclusions = selectedPlan === 'annual' ? annualInclusions : monthlyInclusions;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`relative w-full max-w-md bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl overflow-hidden shadow-2xl transition-all duration-300 ${
          visible ? 'translate-y-0 scale-100' : 'translate-y-6 scale-95'
        }`}
      >
        {/* Gold top accent bar */}
        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 text-[#555] hover:text-[#c9a84c] transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="px-7 pt-7 pb-5 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#c9a84c]/20 to-[#c9a84c]/5 border border-[#c9a84c]/30 flex items-center justify-center">
              <Crown size={28} className="text-[#c9a84c]" fill="#c9a84c" fillOpacity={0.35} />
            </div>
          </div>

          <p className="text-[10px] tracking-[0.28em] uppercase text-[#c9a84c] font-bold mb-2">
            Private Membership
          </p>
          <h2 className="text-xl font-bold text-white leading-tight mb-3">
            Access <span className="text-[#c9a84c]">The Private World</span>
          </h2>
          <p className="text-[13px] text-[#777] leading-relaxed">
            Where the world's most extraordinary things are discovered — curated for those who seek the exceptional, not the ordinary.
          </p>
        </div>

        {/* Plans */}
        <div className="px-7 pb-4 space-y-3">

          {/* Annual Plan */}
          <button
            onClick={() => setSelectedPlan('annual')}
            className={`w-full relative rounded-lg border-2 p-4 text-left transition-all duration-200 ${
              selectedPlan === 'annual' ?'border-[#c9a84c] bg-[#c9a84c]/6' :'border-[#222] bg-[#111] hover:border-[#3a3a3a]'
            }`}
          >
            <div className="absolute -top-3 left-4">
              <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#c9a84c] to-[#e8c96a] text-black text-[10px] font-black tracking-[0.08em] uppercase px-3 py-1 rounded-full shadow-lg">
                <Zap size={9} fill="black" />
                50% OFF — Limited Time This Month
              </span>
            </div>

            <div className="flex items-start justify-between mt-1">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#c9a84c]">Annual</span>
                  <span className="text-[10px] bg-[#c9a84c]/15 text-[#c9a84c] px-2 py-0.5 rounded-full font-semibold">Best Value</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-black text-white">₹1,899</span>
                  <span className="text-[12px] text-[#555]">/ year</span>
                </div>
                <p className="text-[11px] text-[#777] mt-0.5">
                  <span className="text-[#c9a84c] font-semibold">₹158.25/month</span>
                  <span className="ml-1.5 line-through text-[#444]">₹3,599/year</span>
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                selectedPlan === 'annual' ? 'border-[#c9a84c] bg-[#c9a84c]' : 'border-[#333]'
              }`}>
                {selectedPlan === 'annual' && <Check size={11} className="text-black" strokeWidth={3} />}
              </div>
            </div>

            {selectedPlan === 'annual' && (
              <p className="text-[10px] text-[#666] mt-2 leading-relaxed border-t border-[#c9a84c]/10 pt-2">
                Founding member rate — we're offering 50% off to our first members as we open our doors. Regular price of ₹3,599/year applies after this period.
              </p>
            )}
          </button>

          {/* Monthly Plan */}
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={`w-full rounded-lg border-2 p-4 text-left transition-all duration-200 ${
              selectedPlan === 'monthly' ?'border-[#c9a84c] bg-[#c9a84c]/6' :'border-[#222] bg-[#111] hover:border-[#3a3a3a]'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#666] block mb-1">Monthly</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-black text-white">₹299</span>
                  <span className="text-[12px] text-[#555]">/ month</span>
                </div>
                <p className="text-[11px] text-[#555] mt-0.5">Cancel anytime</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                selectedPlan === 'monthly' ? 'border-[#c9a84c] bg-[#c9a84c]' : 'border-[#333]'
              }`}>
                {selectedPlan === 'monthly' && <Check size={11} className="text-black" strokeWidth={3} />}
              </div>
            </div>
          </button>
        </div>

        {/* Inclusions */}
        <div className="px-7 pb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#c9a84c]">What's Included</p>
            {selectedPlan === 'annual' && (
              <span className="inline-flex items-center gap-1 text-[9px] text-[#c9a84c] font-semibold tracking-[0.08em] uppercase">
                <Star size={8} fill="#c9a84c" />
                Full Access
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2">
            {currentInclusions.map((feat) => (
              <div
                key={feat.text}
                className={`flex items-center gap-2.5 rounded-md px-3 py-2 ${
                  feat.highlight
                    ? 'bg-[#c9a84c]/10 border border-[#c9a84c]/30'
                    : 'bg-[#c9a84c]/5 border border-[#c9a84c]/15'
                }`}
              >
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  feat.highlight ? 'bg-[#c9a84c]/30' : 'bg-[#c9a84c]/20'
                }`}>
                  <Check size={10} className="text-[#c9a84c]" strokeWidth={3} />
                </div>
                <span className={`text-[11px] font-medium ${
                  feat.highlight ? 'text-[#c9a84c]' : 'text-[#aaa]'
                }`}>{feat.text}</span>
              </div>
            ))}
          </div>

          {selectedPlan === 'monthly' && (
            <p className="text-[10px] text-[#555] mt-3 text-center">
              Upgrade to Annual for full access to all exclusive benefits
            </p>
          )}
        </div>

        {/* Error */}
        {payuError && (
          <div className="px-7 pb-3">
            <p className="text-[11px] text-red-400 text-center bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {payuError}
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="px-7 pb-7">
          <button
            onClick={handleSubscribe}
            disabled={payuLoading}
            className="w-full py-3.5 bg-gradient-to-r from-[#c9a84c] to-[#e8c96a] text-black font-black text-[13px] tracking-[0.12em] uppercase rounded-lg hover:from-[#b8973b] hover:to-[#d4b555] transition-all duration-200 shadow-lg shadow-[#c9a84c]/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {payuLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Redirecting to PayU...
              </>
            ) : (
              <>
                <Lock size={13} fill="black" fillOpacity={0.4} />
                {user
                  ? (selectedPlan === 'annual' ? 'Pay ₹1,899 via PayU' : 'Pay ₹299 via PayU')
                  : (selectedPlan === 'annual' ? 'Unlock Annual — ₹1,899' : 'Unlock Monthly — ₹299')
                }
              </>
            )}
          </button>
          <p className="text-center text-[10px] text-[#444] mt-3">
            Secure payment via PayU · No hidden fees · Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}
